import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

const Login = () => {
  const { user, loading, accessDenied, signInWithGoogle } = useAuth();
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Parse OAuth errors returned in the URL hash or query string
  useEffect(() => {
    const parse = (raw: string) => {
      const params = new URLSearchParams(raw.replace(/^[#?]/, ""));
      const err = params.get("error_description") || params.get("error");
      return err ? err.replace(/\+/g, " ") : null;
    };
    const fromHash = window.location.hash.includes("error=") ? parse(window.location.hash) : null;
    const fromQuery = window.location.search.includes("error=") ? parse(window.location.search) : null;
    const err = fromHash || fromQuery;
    if (err) {
      setOauthError(err);
      // Clean the URL so refresh doesn't keep showing the error
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(0,0%,9%)]">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(45,98%,59%)]" />
      </div>
    );
  }

  if (user) return <Navigate to="/videos" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(0,0%,9%)] px-6">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="rounded-xl border border-[hsl(0,0%,18%)] bg-[hsl(0,0%,12%)] p-8 shadow-2xl">
          {/* Gold accent bar */}
          <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-[hsl(45,98%,59%)]" />

          <h1 className="text-center text-2xl font-bold tracking-tight text-white">
            Content Engine
          </h1>
          <p className="mt-2 text-center text-sm text-[hsl(0,0%,55%)]">
            Sign in to manage your YouTube content
          </p>

          {/* Google button */}
          <button
            onClick={() => {
              setOauthError(null);
              signInWithGoogle();
            }}
            className="mt-8 flex h-12 w-full items-center justify-center gap-3 rounded-lg bg-white text-sm font-medium text-[hsl(0,0%,15%)] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>

          {accessDenied && (
            <div className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-center text-xs text-red-300">
              This Google account doesn't have access. Contact the team owner for an invite.
            </div>
          )}

          {oauthError && !accessDenied && (
            <div className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-center text-xs text-red-300">
              Sign-in failed: {oauthError}
            </div>
          )}

          <p className="mt-6 text-center text-xs text-[hsl(0,0%,35%)]">
            Access restricted to authorized accounts only
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
